/* Setup building blocks: list chips with the tick-and-add dropdown, −/+
   steppers, indented sub-rows, the status cards and the confirm dialog. */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Btn, Icon } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';

/* ── Chips + "Add lists" ────────────────────────────────────────── */
export interface Opt { id: string; label: string }

/** `accent` tints the chip purple (e.g. include lists next to neutral exclude lists). */
export function Chip({ label, onRemove, accent }: { label: string; onRemove: () => void; accent?: boolean }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <span style={{ height: 24, padding: '0 4px 0 9px', display: 'flex', alignItems: 'center', gap: 4, background: accent ? C.tint16 : C.input, border: `1px solid ${accent ? C.tintLine45 : C.borderHi}`, borderRadius: 6, font: f(500, 12), color: accent ? 'var(--om-accent-4)' : 'var(--om-body)', whiteSpace: 'nowrap' }}>
      {label}
      <button type="button" title={t('remove')} onClick={onRemove} className="cpt-hover-text" style={{ width: 16, height: 16, border: 0, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
        <Icon name="x" size={12} />
      </button>
    </span>
  );
}

const POP_W = 280;
const POP_H = 380; // search + Select all + 256px of rows + footer

/** Multi-tick dropdown: search, ticks, Select all / Clear, Cancel, "Add N". */
export function AddLists({ pool, onAdd, disabled, label, height = 24 }: { pool: Opt[]; onAdd: (ids: string[]) => void; disabled?: boolean; label?: ReactNode; height?: number }) {
  const { t } = useTranslation('assistantCompact');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [at, setAt] = useState<CSSProperties | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const close = () => { setOpen(false); setSel([]); setQ(''); };
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [open]);
  // Portalled and pinned to the button, so a scrolling dialog or a clipped
  // card can't cut the footer off; opens upward when there's no room below.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - POP_W - 8));
      setAt(window.innerHeight - r.bottom < POP_H + 12 && r.top > window.innerHeight - r.bottom
        ? { left, bottom: window.innerHeight - r.top + 6 }
        : { left, top: r.bottom + 6 });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);
  const vis = pool.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()));
  const all = vis.length > 0 && vis.every((o) => sel.includes(o.id));
  const n = sel.length;
  const add = () => { if (n) { onAdd(pool.filter((o) => sel.includes(o.id)).map((o) => o.id)); close(); } };
  return (
    <div style={{ position: 'relative' }}>
      <button ref={btn} type="button" disabled={disabled} onClick={() => setOpen(true)} className="cpt-hover-border" style={{ height, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: `1px dashed ${C.borderHi}`, borderRadius: 6, color: C.purpleLt, font: f(500, 12), cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1 }}>
        <Icon name="plus" size={12} />{label ?? t('su.addLists')}
      </button>
      {open && at && createPortal(
        <div className="cpt-root">
          <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 1400 }} />
          <div style={{ position: 'fixed', ...at, zIndex: 1401, width: POP_W, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 8, borderBottom: `1px solid ${C.divider}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 28, padding: '0 8px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6 }}>
                <Icon name="search" size={12} color={C.muted} />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('su.searchLists')} style={{ flex: 1, minWidth: 0, background: 'transparent', border: 0, outline: 'none', color: C.text, font: f(400, 12.5) }} />
              </div>
            </div>
            {vis.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px 0' }}>
                <button type="button" onClick={() => setSel(all ? sel.filter((id) => !vis.some((o) => o.id === id)) : Array.from(new Set([...sel, ...vis.map((o) => o.id)])))} style={{ border: 0, background: 'transparent', color: C.purpleLt, font: f(500, 11.5), cursor: 'pointer', padding: '2px 4px' }}>
                  {all ? t('su.clear') : t('su.selectAll')}
                </button>
              </div>
            )}
            <div className="cpt-thin-scroll" style={{ maxHeight: 256, overflow: 'auto', padding: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {vis.map((o) => {
                const on = sel.includes(o.id);
                return (
                  <button key={o.id} type="button" className="cpt-hover-row" onClick={() => setSel(on ? sel.filter((x) => x !== o.id) : [...sel, o.id])} style={{ flex: 'none', height: 30, display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', border: 0, borderRadius: 6, background: 'transparent', cursor: 'pointer', textAlign: 'left', font: f(500, 12.5), color: on ? C.text : 'var(--om-body)' }}>
                    <Box on={on} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                  </button>
                );
              })}
              {!vis.length && <span style={{ padding: '10px 8px', font: f(400, 12), color: C.muted }}>{pool.length ? t('su.noMatches') : t('su.allAdded')}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 8, borderTop: `1px solid ${C.divider}` }}>
              <span style={{ flex: 1, font: f(400, 11.5), color: C.muted }}>{n ? t('su.nSelected', { count: n }) : t('su.nAvailable', { count: pool.length })}</span>
              <Btn kind="muted" height={26} onClick={close}>{t('cancel')}</Btn>
              <Btn kind="primary" height={26} onClick={add} style={{ opacity: n ? 1 : 0.45 }}>{n ? t('su.addN', { count: n }) : t('su.add')}</Btn>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export function Box({ on }: { on: boolean }) {
  return (
    <span style={{ width: 14, height: 14, flex: 'none', boxSizing: 'border-box', borderRadius: 4, border: `1.5px solid ${on ? C.purple : C.faint}`, background: on ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name="check" size={10} color={on ? '#fff' : 'transparent'} />
    </span>
  );
}

export function ChipRow({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '0 14px 10px' }}>{children}</div>;
}

/* ── Stepper (28px pill: − value unit +) ────────────────────────── */
export function Stepper({ value, unit, onDec, onInc }: { value: number; unit: string; onDec: () => void; onInc: () => void }) {
  const b = { width: 26, height: 26, border: 0, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' } as const;
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: 28, flex: 'none', boxSizing: 'border-box', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6 }}>
      <button type="button" onClick={onDec} className="cpt-hover-text" style={b}><Icon name="minus" size={12} /></button>
      <span style={{ minWidth: 48, textAlign: 'center', font: f(600, 12.5), color: C.text, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {value}<span style={{ fontWeight: 400, color: C.sub, marginLeft: 3 }}>{unit}</span>
      </span>
      <button type="button" onClick={onInc} className="cpt-hover-text" style={b}><Icon name="plus" size={12} /></button>
    </div>
  );
}

/** An indented settings row under a parent row (follow-up settings). */
export function SubRow({ label, help, helpColor, children }: { label: ReactNode; help?: ReactNode; helpColor?: string; children: ReactNode }) {
  return (
    <div className="cpt-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '8px 14px 8px 34px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ font: f(400, 12.5), color: 'var(--om-body)' }}>{label}</span>
        {help && <span style={{ font: f(400, 11.5), color: helpColor ?? C.muted }}>{help}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>{children}</div>
    </div>
  );
}

/* ── Status card (option 2a: tinted when active) ────────────────── */
export function StatusCard({ icon, label, value, sub, tip, active, subWarn, children }: { icon: string; label: string; value: string; sub: ReactNode; tip?: string; active: boolean; subWarn?: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, background: active ? 'color-mix(in srgb, var(--om-accent) 10%, transparent)' : C.card, border: `1px solid ${active ? C.tintLine45 : C.border}`, borderRadius: 10, minWidth: 0, transition: 'background .2s, border-color .2s' }}>
      <div style={{ width: 40, height: 40, flex: 'none', borderRadius: 10, background: active ? C.purple : C.borderLo, color: active ? '#fff' : C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .2s' }}>
        <Icon name={icon} size={18} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
        <span style={{ font: f(600, 10.5), letterSpacing: '.06em', textTransform: 'uppercase', color: active ? C.purpleLt : C.muted }}>{label}</span>
        <span style={{ font: f(600, 15), color: active ? C.text : C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
        <span title={tip ?? (typeof sub === 'string' ? sub : undefined)} style={{ font: f(400, 12), color: active || subWarn ? C.purplePale : C.sub, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {subWarn ? <Icon name="alert-circle" size={12} style={{ flex: 'none' }} /> : <span style={{ width: 6, height: 6, flex: 'none', borderRadius: 99, background: active ? C.purpleLt : C.faint }} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>
        </span>
      </div>
      {children}
    </div>
  );
}

/** The card's action: outlined on an active card, filled when it needs doing. */
export function CardBtn({ onClick, children, cta }: { onClick: () => void; children: ReactNode; cta?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={cta ? undefined : 'cpt-hover'} style={{ height: 30, flex: 'none', padding: '0 12px', background: cta ? C.purple : 'transparent', border: cta ? 0 : '1px solid color-mix(in srgb, var(--om-accent-2) 35%, transparent)', borderRadius: 6, color: cta ? '#fff' : C.purplePale, font: f(600, 12.5), cursor: 'pointer', whiteSpace: 'nowrap' }}>
      {children}
    </button>
  );
}

/** The big 36×20 on/off switch on the AI assistant card. */
export function BigToggle({ on, onChange, disabled, title }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; title?: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} title={title} disabled={disabled} onClick={() => onChange(!on)} style={{ width: 36, height: 20, flex: 'none', borderRadius: 99, border: 0, padding: 2, background: on ? C.purple : C.toggleOff, cursor: disabled ? 'default' : 'pointer', display: 'flex', opacity: disabled ? 0.6 : 1, transition: 'background .15s' }}>
      <span style={{ width: 16, height: 16, borderRadius: 99, background: '#fff', transform: on ? 'translateX(16px)' : 'none', transition: 'transform .15s' }} />
    </button>
  );
}

/* ── Confirm (protected lists) ──────────────────────────────────── */
export function ConfirmRemove({ title, body, onKeep, onRemove }: { title: string; body: string; onKeep: () => void; onRemove: () => void }) {
  const { t } = useTranslation('assistantCompact');
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onKeep(); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [onKeep]);
  return createPortal(
    <div className="cpt-root" style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div role="dialog" aria-modal="true" style={{ width: 420, maxWidth: '100%', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 10, boxShadow: '0 16px 48px rgba(0,0,0,.55)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 12, padding: '18px 18px 14px' }}>
          <div style={{ width: 36, height: 36, flex: 'none', borderRadius: 9, background: C.tint16, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="shield" size={17} /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <span style={{ font: f(600, 14), color: C.text }}>{title}</span>
            <span style={{ font: f(400, 12.5, 1.5), color: C.sub }}>{body}</span>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 14px', borderTop: `1px solid ${C.divider}` }}>
          <Btn kind="ghost" onClick={onKeep}>{t('su.keepIt')}</Btn>
          <Btn kind="primary" onClick={onRemove}>{t('su.removeAnyway')}</Btn>
        </div>
      </div>
    </div>,
    document.body,
  );
}
